import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { navigationSlice } from 'logic/store/navigation/navigation.slice';
import { Stack } from 'components/stack/stack';
import { Text } from 'components/text/Text';
import styled from 'styled-components';
import tw from 'twin.macro';
import { colors } from 'styles/colors';
import { Breakpoints } from 'logic/hooks/use-breakpoints';
import {
  getAccountRights,
  getAccountType,
} from 'logic/store/authentication/authentication.selectors';
import { AccountRight, AccountType } from 'logic/api-models/api-models';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { Link } from 'gatsby';
import { AppRoute } from 'app/app.route.const';
import { SideBarLinks } from './sidebars';
import {
  getHasLiveEliteSeekerSubscription,
  getOpenEliteratings,
} from '../../logic/store/elitetutor/elitetutor.selectors';
import { elitetutorSlice } from '../../logic/store/elitetutor/elitetutor.slice';
import { SupportHints, useHasSupportHints } from 'components/support-hints';
import { Badge } from 'antd';

export const LayoutSidebar = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${tw`p-2`};
  margin: 0 24px 24px 0;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;

  position: relative;

  grid-area: sidebar;
  background-color: white;

  @media (${Breakpoints.maxDesktop}) {
    display: none;
  }
`;

const PromotionWrapper = styled.div`
  position: absolute;
  bottom: 24px;
`;

const BottomWrapper = styled.div`
  margin: 40px 24px 12px 16px;
`;

type SidebarLinkProps = {
  onClick(): void;
  Icon: any;
  IconActive: any;
  text: string;
  path: string;
  badgeCount?: number;
};

const SidebarLink: React.FC<SidebarLinkProps> = ({
  onClick,
  text,
  Icon,
  IconActive,
  path,
  badgeCount,
}) => {
  const isActive = window?.location.pathname === path;

  return (
    <span onClick={onClick} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
      <span
        style={{
          backgroundColor: isActive ? colors.sidebarActiveRowGary : undefined,
          borderRadius: 11,
          width: '100%',
          height: '100%',
          padding: 8,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ marginRight: 11, flexShrink: 0, lineHeight: 0 }}>
          {isActive ? IconActive : Icon}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <Badge count={badgeCount} offset={[15, 0]}>
            <span style={{ display: 'block' }}>
              <Text size={16}>{text}</Text>
            </span>
          </Badge>
        </span>
      </span>
    </span>
  );
};

const SideBarSubHeadline: React.FC<{ text: string }> = ({ text }) => {
  return (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <span
        style={{
          borderRadius: 11,
          width: '100%',
          height: '100%',
          padding: 8,
        }}
      >
        <Text size={16} color="black">
          {text}
        </Text>
      </span>
    </span>
  );
};

export const Sidebar: FC<{ options: SideBarLinks }> = ({ options }) => {
  const dispatch = useDispatch();
  const accountType = useSelector(getAccountType) || AccountType.STUDENT;
  const accountRights = useSelector(getAccountRights);
  const openEliteratings = useSelector(getOpenEliteratings);
  const hasLiveEliteSeekerSubscription = useSelector(getHasLiveEliteSeekerSubscription);
  const hasSupportHints = useHasSupportHints();
  const eliteSeekerLinks = hasLiveEliteSeekerSubscription
    ? options.eliteSeeker.filter((link) => link.path !== AppRoute.MyPricing)
    : options.eliteSeeker;

  const isElitetutor = accountRights?.includes(AccountRight.ELITETUROR);

  useEffect(() => {
    if (isElitetutor) {
      dispatch(elitetutorSlice.actions.getEliteratings());
    }
  }, [dispatch, isElitetutor]);

  const handleOnLinkClick = (path: string) => {
    dispatch(
      navigationSlice.actions.navigateTo({
        path,
      })
    );
  };

  return (
    <LayoutSidebar>
      <Stack gap={0} flow="row" alignItems="start">
        {options.regular.map((link) => (
          <SidebarLink key={link.path} onClick={() => handleOnLinkClick(link.path)} {...link} />
        ))}
        {options.eliteTutor.length > 0 && (
          <>
            <hr style={{ margin: '8px 16px 8px 16px' }} />
            <SideBarSubHeadline
              key={'tutor'}
              text={translate(translationKeys.sidebar.elitetutor.title)}
            />
          </>
        )}
        {options.eliteTutor.map((link) => {
          if (link.showBadge && link.path === AppRoute.EarnMoney) {
            return (
              <SidebarLink
                badgeCount={openEliteratings.length}
                key={link.path}
                onClick={() => handleOnLinkClick(link.path)}
                {...link}
              />
            );
          } else {
            return (
              <SidebarLink key={link.path} onClick={() => handleOnLinkClick(link.path)} {...link} />
            );
          }
        })}
        {eliteSeekerLinks.length > 0 && (
          <>
            <hr style={{ margin: '8px 16px 8px 16px' }} />
            <SideBarSubHeadline
              key={'tutor'}
              text={translate(translationKeys.sidebar.eliteseeker.title)}
            />
          </>
        )}
        {eliteSeekerLinks.map((link) => (
          <SidebarLink key={link.path} onClick={() => handleOnLinkClick(link.path)} {...link} />
        ))}
      </Stack>
      {accountRights && !accountRights?.length && accountType !== AccountType.TUTORING_INSTITUTE && (
        <PromotionWrapper>
          <Link to={AppRoute.TutorspacePremiumBecome}>
            {translate(translationKeys.sidebar.custom.promotion)}
          </Link>
        </PromotionWrapper>
      )}
      {hasSupportHints && (
        <BottomWrapper>
          <SupportHints />
        </BottomWrapper>
      )}
    </LayoutSidebar>
  );
};
